/* ============================================================
   Artikel Expert — Animationen & Slider (Vanilla JS, kein jQuery)
   - Scroll-Reveals (IntersectionObserver, einmalig)
   - Wort-Stagger für Überschriften
   - Counter-Animation (Über-uns-Kennzahlen)
   - Karussells für Ablauf & Versprechen (Scroll-Snap + Autoplay + Dots)
   Alles idempotent (Theme-Editor-Reloads) und mit
   prefers-reduced-motion-Fallback.
   ============================================================ */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Helpers ---------- */
  function qa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }
  function once(el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  /* ---------- 1. Scroll-Reveals ---------- */
  var revealObserver = null;
  function getRevealObserver() {
    if (revealObserver) return revealObserver;
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('ae-in');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    return revealObserver;
  }

  function tagReveal(el, variant, delay) {
    if (!el || !once(el, 'aeReveal')) return;
    el.classList.add('ae-reveal');
    if (variant) el.classList.add('ae-reveal--' + variant);
    if (delay) el.style.setProperty('--ae-d', delay + 'ms');
    if (REDUCED) { el.classList.add('ae-in'); return; }

    /* Elemente im Start-Viewport sofort einblenden (robust, ohne Observer) —
       der Observer übernimmt nur Inhalte unterhalb des Folds. */
    var rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setTimeout(function () { el.classList.add('ae-in'); }, 60);
      return;
    }
    getRevealObserver().observe(el);
  }

  function splitWords(el) {
    if (!el || !once(el, 'aeSplit')) return;
    if (REDUCED) return;
    var words = el.textContent.split(/\s+/).filter(Boolean);
    if (!words.length || words.length > 24) return;
    el.textContent = '';
    words.forEach(function (word, i) {
      var span = document.createElement('span');
      span.className = 'ae-w';
      span.style.setProperty('--ae-wd', (i * 55) + 'ms');
      span.textContent = word;
      el.appendChild(span);
      el.appendChild(document.createTextNode(' '));
    });
  }

  function initReveals(root) {
    root = root || document;

    /* Überschriften & Sektions-Köpfe */
    qa('.ae-section .ae-eyebrow', root).forEach(function (el) { tagReveal(el, null, 0); });
    qa('.ae-section .ae-h1, .ae-section .ae-h2', root).forEach(function (el) {
      splitWords(el);
      tagReveal(el, null, 80);
    });

    /* Hero */
    qa('.ae-hero__grid > div:first-child > p', root).forEach(function (el) { tagReveal(el, null, 180); });
    qa('.ae-hero__ctas', root).forEach(function (el) { tagReveal(el, null, 280); });
    qa('.ae-trust', root).forEach(function (el) { tagReveal(el, null, 380); });
    qa('.ae-hero__img', root).forEach(function (el) { tagReveal(el, 'right', 200); });

    /* Bilder & große Blöcke */
    qa('.ae-about__img', root).forEach(function (el) { tagReveal(el, 'zoom', 100); });
    qa('.ae-cta__grid > div:first-child', root).forEach(function (el) { tagReveal(el, 'left', 0); });
    qa('.ae-rufcheck__card', root).forEach(function (el) { tagReveal(el, 'up-big', 0); });

    /* Gestaffelte Karten/Listen */
    var staggered = [
      '.ae-checklist li',
      '.ae-counterbox',
      '.ae-service-row',
      '.ae-step-card',
      '.ae-price-card',
      '.ae-team-card',
      '.ae-promise-card',
      '.ae-blog-card'
    ];
    staggered.forEach(function (sel) {
      qa(sel, root).forEach(function (el) {
        var idx = Array.prototype.indexOf.call(el.parentNode.children, el);
        tagReveal(el, null, (idx % 6) * 90);
      });
    });
  }

  /* ---------- 2. Counter ---------- */
  function animateCounter(el) {
    var raw = el.textContent;
    var match = raw.match(/\d[\d.,]*/);
    if (!match) return;
    var target = parseInt(match[0].replace(/[.,]/g, ''), 10);
    if (isNaN(target)) return;
    var prefix = raw.slice(0, match.index);
    var suffix = raw.slice(match.index + match[0].length);
    var duration = 1300;
    var start = null;
    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = prefix + Math.round(target * eased) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters(root) {
    var els = qa('.ae-counterbox h3', root || document);
    if (!els.length) return;
    if (REDUCED) return;
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) {
      if (once(el, 'aeCounter')) obs.observe(el);
    });
  }

  /* ---------- 3. Karussell (Scroll-Snap + Autoplay + Dots) ---------- */
  function buildCarousel(track, opts) {
    if (!track || !once(track, 'aeCarousel')) return;
    var items = Array.prototype.slice.call(track.children);
    if (items.length < 2) return;

    var wrapper = document.createElement('div');
    wrapper.className = 'ae-carousel';
    track.parentNode.insertBefore(wrapper, track);
    wrapper.appendChild(track);
    track.classList.add('ae-carousel__track');
    track.style.setProperty('--ae-pv-d', opts.desktop);
    track.style.setProperty('--ae-pv-t', opts.tablet);
    items.forEach(function (item) { item.classList.add('ae-carousel__item'); });

    var dotsNav = document.createElement('div');
    dotsNav.className = 'ae-dots';
    dotsNav.setAttribute('role', 'tablist');
    wrapper.appendChild(dotsNav);

    var current = 0;
    var timer = null;
    var dots = [];

    function perView() {
      var w = window.innerWidth;
      if (w <= 599) return 1;
      if (w <= 989) return opts.tablet;
      return opts.desktop;
    }
    function maxIndex() { return Math.max(items.length - perView(), 0); }

    function rebuildDots() {
      dotsNav.innerHTML = '';
      dots = [];
      for (var i = 0; i <= maxIndex(); i++) {
        (function (idx) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'ae-dots__dot';
          b.setAttribute('aria-label', 'Slide ' + (idx + 1));
          b.addEventListener('click', function () { goTo(idx); restart(); });
          dotsNav.appendChild(b);
          dots.push(b);
        })(i);
      }
      paint();
    }

    function paint() {
      dots.forEach(function (d, i) { d.classList.toggle('ae-dots__dot--active', i === Math.min(current, dots.length - 1)); });
    }

    function goTo(idx) {
      current = Math.max(0, Math.min(idx, maxIndex()));
      var target = items[current];
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: REDUCED ? 'auto' : 'smooth' });
      paint();
    }

    function next() { goTo(current >= maxIndex() ? 0 : current + 1); }

    function restart() {
      if (timer) clearInterval(timer);
      if (REDUCED) return;
      timer = setInterval(function () {
        if (document.visibilityState !== 'visible') return;
        if (wrapper.matches(':hover')) return; /* Pause bei Hover — zustandslos */
        var r = wrapper.getBoundingClientRect();
        if (r.bottom < 0 || r.top > window.innerHeight) return; /* außerhalb des Viewports */
        next();
      }, 4500);
    }

    /* Index aus Scroll-Position ableiten (Touch/Drag) */
    var scrollDebounce = null;
    track.addEventListener('scroll', function () {
      if (scrollDebounce) clearTimeout(scrollDebounce);
      scrollDebounce = setTimeout(function () {
        var step = items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : 1;
        if (step > 0) {
          current = Math.max(0, Math.min(Math.round(track.scrollLeft / step), maxIndex()));
          paint();
        }
      }, 80);
    }, { passive: true });

    var resizeDebounce = null;
    window.addEventListener('resize', function () {
      if (resizeDebounce) clearTimeout(resizeDebounce);
      resizeDebounce = setTimeout(function () { rebuildDots(); goTo(Math.min(current, maxIndex())); }, 150);
    });

    rebuildDots();
    restart();
  }

  function initCarousels(root) {
    root = root || document;
    qa('.ae-steps', root).forEach(function (track) {
      buildCarousel(track, { desktop: 3, tablet: 2 });
    });
    qa('.ae-promises', root).forEach(function (track) {
      buildCarousel(track, { desktop: 2, tablet: 2 });
    });
  }

  /* ---------- Init ---------- */
  function initAll(root) {
    initReveals(root);
    initCounters(root);
    initCarousels(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Shopify-Theme-Editor: Sections werden live neu gerendert */
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
})();
